import React, { useState, useEffect, useRef } from 'react';
import cn from 'classnames';
import capitalize from 'lodash/capitalize';
import { useDispatch, useSelector } from 'react-redux';
// Components
import { DropDownButton } from '@progress/kendo-react-buttons';
import { BsMoon, BsSun } from 'react-icons/bs';
import { NavLink, useHistory } from 'react-router-dom';
import SearchBoxToggle from 'shared/SearchBoxToggle';
import Button from 'shared/buttons';
import { SearchDriverDispatchesButton } from 'src/dispatching/SearchDriverDispatches';
import { OrderSupportRequestsPopupAction } from 'shared/layout/RequestsPopup';
// Styles
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { switchTheme } from 'src/theme';
import moment from 'moment';
import useCurrentUser from 'shared/../hooks/useCurrentUser';
import { CREATE_ORDER } from '../../../constants/UserAccess';

const logo = require('shared/../imgs/gwi-logo.png');
const darkLogo = require('shared/../imgs/gwi-logo-dark-mode.png');

const ThemeToggle = ({ mode, onToggle }) => (
  <div title={`Switch to ${mode === 'dark' ? 'light' : 'dark'}`}>
    <Button
      iconComponent={mode === 'dark' ? <BsMoon /> : <BsSun />}
      onClick={onToggle}
      themeColor={mode === 'dark' ? 'warning' : 'tertiary'}
      iconOnly
      fillMode='flat'
    />
  </div>
);

const MenuButton = ({ onClick }) => (
  <div className='nav-button-container'>
    <Button iconOnly onClick={onClick} fillMode='flat' icon='menu' />
  </div>
);

const Logo = ({ environmentName, mode }) => {
  const imgClassName = cn('logo-image', { dark: mode === 'dark' });
  const envBannerClassName = cn('env-banner', { [environmentName]: !!environmentName });

  const interval = useRef();
  const [time, setTime] = useState('');

  useEffect(() => {
    if (environmentName) {
      interval.current = setInterval(() => {
        setTime(`${moment().format('MM/DD/YYYY HH:mm:ss Z')}`);
      }, 1000);
    } else {
      setTime('');
    }

    return () => {
      if (interval?.current) {
        clearInterval(interval.current);
      }
    };
  }, [environmentName]);

  return (
    <div className='logo-container'>
      <NavLink exact to='/' className='logo-link'>
        <img src={mode === 'dark' ? darkLogo : logo} alt='Gulf Winds' className={imgClassName} />
      </NavLink>
      {environmentName && <span className={envBannerClassName}>{`${capitalize(environmentName)} - ${`${time}`}`}</span>}
    </div>
  );
};

const Header = ({ onNavOpen, version, onFilterOpened, onFavouriteOrdersOpened, onCustomSearchOpened }) => {
  const themeMode = useSelector(({ app }) => app?.themeMode);
  const dispatch = useDispatch();

  const onThemeToggle = () => {
    switchTheme(null, dispatch);
  };

  const history = useHistory();
  const location = useLocation();
  const { hasAccessTo } = useCurrentUser();

  const items = hasAccessTo(CREATE_ORDER) ? ['Manual', 'Order Wizard', 'Upload', 'EDI', 'Quote'] : ['Quote'];

  const quickNavigation = (e) => {
    switch (e.item) {
    case 'Manual':
      history.push('/orders/master-order?manual=true');
      break;
    case 'Order Wizard':
      history.push('/orders/master-order');
      break;
    case 'Upload':
      history.push('/orders/order-uploads');
      break;
    case 'EDI':
      history.push('/orders/pending-orders');
      break;
    case 'Quote': {
      const path = '/pricing/quotes/new';
      if (location.pathname === path) {
        window?.location?.reload();
      } else {
        history.push(path);
      }
      break;
    }
    default:
      break;
    }
  };

  const environmentName = version?.environment ? version.environment.toLowerCase() : null;
  const headerClassName = cn('header-content', { [environmentName]: !!environmentName });

  return (
    <header role='heading' aria-level='1' className={headerClassName}>
      <div style={{ display: 'flex' }}>
        <MenuButton onClick={onNavOpen} />
        <Logo environmentName={environmentName} mode={themeMode} />
      </div>
      <div
        style={{
          display: 'flex',
          gap: '0.25rem',
          marginLeft: 'auto',
          alignItems: 'center',
        }}
      >
        <SearchBoxToggle key='searchBox' />
        <Button icon='search' title='Custom Search' onClick={onCustomSearchOpened} />
        <ThemeToggle onToggle={onThemeToggle} mode={themeMode} />
        <div title='Filter'>
          <Button
            id='filter-button'
            icon='filter'
            themeColor='tertiary'
            fillMode='flat'
            onClick={onFilterOpened}
            tooltip='Filter'
          />
        </div>
        <div title='Quick Add'>
          <DropDownButton
            icon='plus'
            title='Quick Add'
            fillMode='flat'
            items={items}
            onItemClick={quickNavigation}
            themeColor='tertiary'
          />
        </div>
        <div title='Favourite Orders'>
          <Button
            icon='star'
            onClick={onFavouriteOrdersOpened}
            tooltip='Favorites'
            fillMode='flat'
            themeColor='warning'
          />
        </div>
        <div title='Search Dispatches by Driver'><SearchDriverDispatchesButton /></div>
        <OrderSupportRequestsPopupAction />
      </div>
    </header>
  );
};

export default Header;
